import React, { useMemo } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import slugify from "@sindresorhus/slugify";
import classNames from "classnames";
import Breadcrumb from "./breadcrumb";
import { Link } from "./link";
import sortPosition from "../utility/sort-position";
import IframeResizer from "iframe-resizer-react";
import Parser, { domToReact, attributesToProps } from "html-react-parser";

const replaceIframe = (node) => {
  if (node.type === "tag" && node.name === "iframe") {
    if (node.attribs.src?.startsWith("https://www.google.com/maps/embed")) {
      const props = attributesToProps(node.attribs);
      const { title = "Google Map" } = props;
      return (
        <iframe {...props} title={title}>
          {domToReact(node.children)}
        </iframe>
      );
    }
    return (
      <IframeResizer
        log
        onMessage={(data) => console.log("message: ", JSON.stringify(data))}
        checkOrigin={false}
        src={node.attribs.src}
        style={{ width: "1px", minWidth: "100%", border: "none" }}
        referrerPolicy="strict-origin-when-cross-origin"
      />
    );
  }
};

const EditorialIntro = ({ data, breadcrumb, isFirst, origin }) => {
  const modules = useMemo(
    () =>
      data.moduleTitleFieldSet.length
        ? data.moduleTitleFieldSet
            .filter(
              ({ editorialTitle, abstract, imageFieldSet, cta }) =>
                editorialTitle || abstract || imageFieldSet || (cta?.ctalabel && cta?.ctaLink)
            )
            .sort(sortPosition)
        : data.moduleTitleFieldSet,
    [data.moduleTitleFieldSet]
  );

  const { labelManagerLabels } = useStaticQuery(graphql`
    {
      labelManagerLabels: labelManagerLabels {
        discoverMore
      }
    }
  `);

  return (
    <>
      {breadcrumb && <Breadcrumb className="breadcrumb--dark py-3" breadcrumb={breadcrumb} />}
      {modules.map(
        ({ editorialTitle, abstract, textPosition, anchorDesktop, imageFieldSet, cta }, i) => (
          <React.Fragment key={i}>
            {anchorDesktop && <span id={slugify(anchorDesktop)} className="tabs__scroll-anchor" />}
            <section
              className={classNames(
                "section editorial-intro",
                origin === "assistance-footer" && "editorial-intro--assistance-footer"
              )}
            >
              {editorialTitle && (
                <div className="container-fluid">
                  <div className="row">
                    <div className="col-12 text-center">
                      {isFirst ? (
                        <h1 className="editorial-intro__title heading-sm-semibold lg-heading-md-semibold mb-4">
                          {editorialTitle}
                        </h1>
                      ) : (
                        <h2 className="editorial-intro__title heading-sm-semibold lg-heading-md-semibold mb-4">
                          {editorialTitle}
                        </h2>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {abstract && (
                <div className="container">
                  <div className="row">
                    <div className="col-12">
                      <div
                        className={classNames(
                          `editorial-intro__abstract text-sm-light${
                            textPosition?.[0] === "Lateral" ? "" : " text-center"
                          } mx-auto`,
                          origin === "assistance-footer" &&
                            "editorial-intro__abstract--assistance-footer"
                        )}
                      >
                        {" "}
                        {Parser(abstract, { replace: replaceIframe })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {imageFieldSet?.image && (
                <div className="container-fluid">
                  <div className="row mt-3">
                    <div className="col-12">
                      <GatsbyImage
                        className="editorial-intro__image"
                        image={getImage(imageFieldSet.image.node)}
                        alt={imageFieldSet.altImage || ""}
                      />
                    </div>
                  </div>
                </div>
              )}
              {cta?.ctalabel && cta?.ctaLink && (
                <div className="mt-4 text-center">
                  <Link
                    to={cta.ctaLink}
                    className={classNames(
                      `btn-link button`,
                      cta.backgroundColor?.[0] === "White" && "btn-link--white secondary", // Figma design explicitly says not to use white on white
                      cta.backgroundColor?.[0] === "Blu" && "btn-link--blue primary",
                      cta.backgroundColor?.[0] === "Black" && "btn-link--black secondary"
                    )}
                  >
                    {cta.ctalabel || labelManagerLabels.discoverMore}
                  </Link>
                </div>
              )}
            </section>
          </React.Fragment>
        )
      )}
    </>
  );
};

export default EditorialIntro;
export const fragment = graphql`
  fragment EditorialFragment on LiferayEditorial {
    id
    liferayFields {
      siteId
      articleId
      typeName
    }
    moduleTitleFieldSet {
      editorialTitle: title
      abstract
      position
      textPosition
      anchorDesktop
      anchorMobile
      imageFieldSet {
        image {
          node {
            gatsbyImageData(layout: FULL_WIDTH)
          }
        }
        altImage
      }
      cta {
        ctalabel
        backgroundColor
        ctaLink
      }
    }
  }
`;
